import * as Sentry from "@sentry/react";
import { HelmetProvider } from "react-helmet-async";
import { Toaster } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider } from "./contexts/AuthContext";
import { AuthModalProvider } from "./contexts/AuthModalContext";
import { CreditsProvider } from "./contexts/CreditsContext";
import { LanguageProvider } from "./contexts/LanguageContext";
import { ThemeProvider } from "./contexts/ThemeContext";
import { CookieConsentProvider } from "./contexts/CookieConsentContext";
import { GeneratedImagesProvider } from "./contexts/GeneratedImagesContext";
import { FeatureRequestDialogProvider } from "./contexts/FeatureRequestDialogContext";
import { UploadErrorDialogProvider } from "./contexts/UploadErrorDialogContext";
import { CookieConsentSync } from "./components/flowly/CookieConsentSync/CookieConsentSync";
import { PricingModal } from "./components/flowly/PricingModal/PricingModal";
import { AuthModal } from "./components/flowly/AuthModal/AuthModal";
import { MarketingConsentModal } from "./components/flowly/MarketingConsentModal/MarketingConsentModal";
import { WhatsAppCommunityModal } from "./components/flowly/WhatsAppCommunity/WhatsAppCommunityModal";
import { VerifyEmailModal } from "./components/flowly/VerifyEmailModal/VerifyEmailModal";
import { FeatureRequestDialog } from "./components/flowly/FeatureRequestDialog/FeatureRequestDialog";
import { UploadErrorDialog } from "./components/flowly/UploadErrorDialog/UploadErrorDialog";
import { ProtectedRoute } from "./components/flowly/ProtectedRoute/ProtectedRoute";
import { AdminRoute } from "./components/flowly/AdminRoute/AdminRoute";
import Layout from "./components/flowly/Layout/Layout";
import { COMMUNITY_ENABLED } from "./config/featureFlags";
import { lazyRoute } from "./lib/lazyRoute";
import { queryClient } from "./lib/queryClient";
import { ErrorFallback } from "./components/flowly/ErrorFallback/ErrorFallback";
import { LanguageRouteSync } from "./contexts/LanguageRouteSync";

// Pages are route-level code-split — each becomes its own chunk, so the initial
// load only ships the Landing page plus shared deps. The <Suspense> boundary
// lives in Layout, around <Outlet />.
const Landing = lazyRoute(() => import("./pages/Landing/Landing"));
const Dashboard = lazyRoute(() => import("./pages/Dashboard/Dashboard"));
const GenerateProductCatalog = lazyRoute(() => import("./pages/GenerateProductCatalog/GenerateProductCatalog"));
const ImageGenerator = lazyRoute(() => import("./pages/ImageGenerator/ImageGenerator"));
const FramesToVideo = lazyRoute(() => import("./pages/FramesToVideo/FramesToVideo"));
const TextToVideo = lazyRoute(() => import("./pages/TextToVideo/TextToVideo"));
const Explore = lazyRoute(() => import("./pages/Explore/Explore"));
const Pricing = lazyRoute(() => import("./pages/Pricing/Pricing"));
const Profile = lazyRoute(() => import("./pages/Profile/Profile"));
const Gallery = lazyRoute(() => import("./pages/Gallery/Gallery"));
const ImageDirectorChat = lazyRoute(() => import("./pages/ImageDirectorChat/ImageDirectorChat"));
const ProfilePhoto = lazyRoute(() => import("./pages/ProfilePhoto/ProfilePhoto"));
const ProductCreative = lazyRoute(() => import("./pages/ProductCreative/ProductCreative"));
const TalkingAvatar = lazyRoute(() => import("./pages/TalkingAvatar/TalkingAvatar"));
const BaseballTrend = lazyRoute(() => import("./pages/BaseballTrend/BaseballTrend"));
const BotBuilder = lazyRoute(() => import("./pages/BotBuilder/BotBuilder"));
const BotEmbed = lazyRoute(() => import("./pages/BotEmbed/BotEmbed"));
const Share = lazyRoute(() => import("./pages/Share/Share"));
const ReferralLanding = lazyRoute(() => import("./pages/ReferralLanding/ReferralLanding"));
const Invite = lazyRoute(() => import("./pages/Invite/Invite"));
const PaymentResult = lazyRoute(() => import("./pages/PaymentResult/PaymentResult"));
const Checkout = lazyRoute(() => import("./pages/Checkout/Checkout"));
const Terms = lazyRoute(() => import("./pages/Terms/Terms"));
const Privacy = lazyRoute(() => import("./pages/Privacy/Privacy"));
const Accessibility = lazyRoute(() => import("./pages/Accessibility/Accessibility"));
const Contact = lazyRoute(() => import("./pages/Contact/Contact"));
const Help = lazyRoute(() => import("./pages/Help/Help"));
const Admin = lazyRoute(() => import("./pages/Admin/Admin"));
const ComparePage = lazyRoute(() => import("./pages/Compare/ComparePage"));
const NotFound = lazyRoute(() => import("./pages/NotFound/NotFound"));

const App = () => (
  <Sentry.ErrorBoundary fallback={<ErrorFallback />}>
    <HelmetProvider>
    <QueryClientProvider client={queryClient}>
      <ThemeProvider>
      <CookieConsentProvider>
        <AuthProvider>
        <CookieConsentSync />
        <AuthModalProvider>
          <CreditsProvider>
            <LanguageProvider>
              <GeneratedImagesProvider>
                <FeatureRequestDialogProvider>
                <UploadErrorDialogProvider>
                <TooltipProvider>
                  <Toaster />
                  <BrowserRouter future={{ v7_startTransition: true }}>
                    <LanguageRouteSync />
                    <PricingModal />
                    <AuthModal />
                    <MarketingConsentModal />
                    <WhatsAppCommunityModal />
                    <VerifyEmailModal />
                    <FeatureRequestDialog />
                    <UploadErrorDialog />
                    <Routes>
                      <Route element={<Layout />}>
                        <Route path="/" element={<Landing />} />
                        {/* Community gallery — gated by COMMUNITY_ENABLED in src/config/featureFlags.ts.
                            When false: both routes redirect to Landing. Flip the flag to restore. */}
                        <Route
                          path="/explore"
                          element={COMMUNITY_ENABLED ? <Explore /> : <Navigate to="/" replace />}
                        />
                        <Route
                          path="/dashboard"
                          element={COMMUNITY_ENABLED ? <Explore /> : <Navigate to="/" replace />}
                        />
                        {/* Studio flows are OPEN to anonymous users so they
                            can see the UI and configure the no-upload bits.
                            Anything that costs credits (preview/render/generate
                            endpoints) still enforces auth server-side, and the
                            client-side useAnonymousGate intercepts the upload
                            and generate buttons to prompt signup. */}
                        <Route path="/generate-product-catalog" element={<GenerateProductCatalog />} />
                        <Route path="/generate-image" element={<ImageGenerator />} />
                        <Route path="/frames-to-video" element={<FramesToVideo />} />
                        <Route path="/text-to-video" element={<TextToVideo />} />
                        <Route path="/pricing" element={<Pricing />} />
                        <Route
                          path="/checkout"
                          element={
                            <ProtectedRoute>
                              <Checkout />
                            </ProtectedRoute>
                          }
                        />
                        <Route
                          path="/payment/result"
                          element={
                            <ProtectedRoute>
                              <PaymentResult />
                            </ProtectedRoute>
                          }
                        />
                        <Route
                          path="/profile"
                          element={
                            <ProtectedRoute>
                              <Profile />
                            </ProtectedRoute>
                          }
                        />
                        <Route
                          path="/invite"
                          element={
                            <ProtectedRoute>
                              <Invite />
                            </ProtectedRoute>
                          }
                        />
                        <Route path="/gallery" element={<Gallery />} />
                        <Route path="/image-director-chat" element={<ImageDirectorChat />} />
                        <Route path="/profile-photo" element={<ProfilePhoto />} />
                        <Route path="/product-creative" element={<ProductCreative />} />
                        <Route path="/talking-avatar" element={<TalkingAvatar />} />
                        <Route path="/baseball-trend" element={<BaseballTrend />} />
                        <Route
                          path="/bot-builder"
                          element={
                            <ProtectedRoute>
                              <BotBuilder />
                            </ProtectedRoute>
                          }
                        />
                        {/* Comparison pages — `/compare/<slug>` (e.g. midjourney-vs-todo-ai).
                            Each slug is also in PUBLIC_ROUTES so the prerenderer snapshots
                            it as static HTML. Unknown slugs redirect to /404 in-component. */}
                        <Route path="/compare/:slug" element={<ComparePage />} />
                        {/* Public share page — branded /s/:id links replace raw CDN shares */}
                        <Route path="/s/:id" element={<Share />} />
                        {/* Referral invite landing — /r/:code */}
                        <Route path="/r/:code" element={<ReferralLanding />} />
                        {/* Redirect old route */}
                        <Route
                          path="/studio/image-director-chat"
                          element={
                            <Navigate to="/image-director-chat" replace />
                          }
                        />
                        <Route path="/terms" element={<Terms />} />
                        <Route path="/privacy" element={<Privacy />} />
                        <Route path="/accessibility" element={<Accessibility />} />
                        <Route path="/contact" element={<Contact />} />
                        <Route path="/help" element={<Help />} />
                        <Route
                          path="/admin"
                          element={
                            <AdminRoute>
                              <Admin />
                            </AdminRoute>
                          }
                        />

                        {/* ── Hebrew URL mirror — /he/<route> serves the Hebrew
                            prerendered HTML for crawlers and AI bots that
                            cannot run JavaScript. React renders the same
                            components; the LanguageProvider sees the `/he`
                            prefix and forces language=he on first paint.
                            Every public route is mirrored; gated routes are
                            mirrored too so the navbar's language toggle keeps
                            working symmetrically while in /profile, /admin, …
                            See src/contexts/LanguageContext.tsx for the prefix
                            detection logic. */}
                        <Route path="/he" element={<Landing />} />
                        <Route path="/he/explore" element={COMMUNITY_ENABLED ? <Explore /> : <Navigate to="/he" replace />} />
                        <Route path="/he/dashboard" element={COMMUNITY_ENABLED ? <Explore /> : <Navigate to="/he" replace />} />
                        <Route path="/he/generate-product-catalog" element={<GenerateProductCatalog />} />
                        <Route path="/he/generate-image" element={<ImageGenerator />} />
                        <Route path="/he/frames-to-video" element={<FramesToVideo />} />
                        <Route path="/he/text-to-video" element={<TextToVideo />} />
                        <Route path="/he/pricing" element={<Pricing />} />
                        <Route path="/he/checkout" element={<ProtectedRoute><Checkout /></ProtectedRoute>} />
                        <Route path="/he/payment/result" element={<ProtectedRoute><PaymentResult /></ProtectedRoute>} />
                        <Route path="/he/profile" element={<ProtectedRoute><Profile /></ProtectedRoute>} />
                        <Route path="/he/invite" element={<ProtectedRoute><Invite /></ProtectedRoute>} />
                        <Route path="/he/gallery" element={<Gallery />} />
                        <Route path="/he/image-director-chat" element={<ImageDirectorChat />} />
                        <Route path="/he/profile-photo" element={<ProfilePhoto />} />
                        <Route path="/he/product-creative" element={<ProductCreative />} />
                        <Route path="/he/talking-avatar" element={<TalkingAvatar />} />
                        <Route path="/he/baseball-trend" element={<BaseballTrend />} />
                        <Route path="/he/bot-builder" element={<ProtectedRoute><BotBuilder /></ProtectedRoute>} />
                        <Route path="/he/compare/:slug" element={<ComparePage />} />
                        <Route path="/he/terms" element={<Terms />} />
                        <Route path="/he/privacy" element={<Privacy />} />
                        <Route path="/he/accessibility" element={<Accessibility />} />
                        <Route path="/he/contact" element={<Contact />} />
                        <Route path="/he/help" element={<Help />} />
                        <Route path="/he/admin" element={<AdminRoute><Admin /></AdminRoute>} />
                      </Route>
                      {/* Bot Builder iframe — standalone (no navbar / footer
                          / TodoBot button). Embedded on customer websites. */}
                      <Route path="/embed/bot/:botId" element={<BotEmbed />} />
                      <Route path="*" element={<NotFound />} />
                    </Routes>
                  </BrowserRouter>
                </TooltipProvider>
                </UploadErrorDialogProvider>
                </FeatureRequestDialogProvider>
              </GeneratedImagesProvider>
            </LanguageProvider>
          </CreditsProvider>
        </AuthModalProvider>
        </AuthProvider>
      </CookieConsentProvider>
    </ThemeProvider>
    </QueryClientProvider>
    </HelmetProvider>
  </Sentry.ErrorBoundary>
);

export default App;
